import { computed, onMounted, ref } from "@vue/composition-api";
import { MessagePrices } from "@scrile/api-provider/dist/api/MessagePricesProvider";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { ErrorDTO } from "@scrile/api-provider/dist/lib/apiErrorHandler";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import usePaymentPackage from "@/hooks/usePaymentPackage";
import providers from "@/lib/api";
import createDeferred from "@/lib/deferredPromise";
import config from "@/config";

const usePaidMessages = (userId?: string) => {
  const paymentPackages = ref<PaymentPackage[]>([]);
  const { findAll: findAllPaymentPackages } = usePaymentPackage();
  const getPaymentPackages = async () => {
    paymentPackages.value = await findAllPaymentPackages([PaymentPackageEnum.PER_SENT_MESSAGE]);
  };
  const messagePrices = ref<MessagePrices>();
  const getPriceData = async (userId: string) => {
    const response = await providers.MessagePricesProvider.find({ userIds: [userId] });
    messagePrices.value = response.find(i => i.userId === userId);
  };
  const priceAmount = computed(
    () => paymentPackages.value.find(i => i.id === messagePrices.value?.perSentPriceId)?.amount ?? 0
  );
  const priceMediaAmount = computed(
    () => paymentPackages.value.find(i => i.id === messagePrices.value?.perSentWithAttachmentPriceId)?.amount ?? 0
  );

  const processingPayment = ref(false);
  const error = ref(false);
  const payForMessage = async (recipientId: string, hasAttachments: boolean) => {
    processingPayment.value = true;
    providers.PayPerMessageProvider.setReceiveErrors(false);
    try {
      return await providers.PayPerMessageProvider.payPerMessage({ recipientId, attachment: hasAttachments });
    } catch (e) {
      if (
        (e as ErrorDTO[]).some(
          i => i?.details?.messageTemplate === "{com.scrile.money.validation.SpendingBalanceValidator.message}"
        )
      ) {
        error.value = true;
        return null;
      }
      throw e;
    } finally {
      processingPayment.value = false;
    }
  };
  const closeError = () => {
    error.value = false;
  };

  const { authUser, authorities } = useAuthUser();
  const isHasAuthoritiesToSendMessages = computed(() =>
    checkAuthorities(authorities.value, ["sendFreeMessageToPaidThreads"])
  );
  onMounted(() => {
    if (!userId || !config.featurePaidSentMessage || !authUser.value || isHasAuthoritiesToSendMessages.value) {
      return;
    }
    getPriceData(userId);
    getPaymentPackages();
  });

  const deferred = createDeferred();
  const showPaymentConfirm = ref(false);
  const showPaymentMediaConfirm = ref(false);

  const onPaymentConfirm = (response: boolean) => {
    showPaymentConfirm.value = false;
    deferred.resolve(response);
    const newDeferred = createDeferred();
    deferred.promise = newDeferred.promise;
    deferred.resolve = newDeferred.resolve;
  };

  const createSendPaidMessage = (
    sendMessage: (text: string) => Promise<Message[] | undefined>,
    hasAttachments: boolean
  ) => {
    if (!config.featurePaidSentMessage || isHasAuthoritiesToSendMessages.value || !userId) {
      return sendMessage;
    }
    return async (text: string) => {
      if (!userId) {
        throw new Error("UserId required");
      }
      try {
        processingPayment.value = true;
        await getPriceData(userId);
        if (priceAmount.value > 0 || (hasAttachments && priceMediaAmount.value > 0)) {
          if (hasAttachments && priceMediaAmount.value > priceAmount.value) {
            showPaymentMediaConfirm.value = true;
          } else {
            showPaymentConfirm.value = true;
          }
          if (!(await deferred.promise)) {
            return;
          }
          const transaction = await payForMessage(userId, hasAttachments);
          if (!transaction) {
            return;
          }
        }
        return await sendMessage(text);
      } finally {
        processingPayment.value = false;
        showPaymentConfirm.value = false;
        showPaymentMediaConfirm.value = false;
      }
    };
  };

  return {
    createSendPaidMessage,
    priceAmount,
    priceMediaAmount,
    processingPayment,
    showPaymentConfirm,
    showPaymentMediaConfirm,
    onPaymentConfirm,
    error,
    closeError,
  };
};

export default usePaidMessages;
